/**
 * DraggableMentalityFab — iOS AssistiveTouch-style floating button.
 *
 * Hold-and-drag to reposition; release snaps to the nearest viewport
 * edge. Short taps (<6px drift) still open the overlay. Position
 * persists across reloads via the `useDragPosition` SSOT.
 *
 * Snap targets: all four edges, on both desktop and mobile. The left
 * edge is anchored to `.layout-main` inset so the sidebar column
 * doesn't render over the FAB. The top edge clears the SurfaceHeader
 * (sticky 64px) so the FAB stays interactive. The bottom edge keeps
 * a small `FAB_MARGIN` gap from the viewport edge — no special
 * composer clearance, since the FAB is a global affordance and
 * mustn't have dead-zones on non-chat pages.
 *
 * Architecture
 * ------------
 * Persistence and viewport-clamp live in `useDragPosition` (the SSOT).
 * Snap-to-edge is a pure derivation: on drag-end we compute the snap
 * target from `{x, y}` + viewport via `computeSnapTarget`, then commit
 * the snapped coordinates. Storage stores `{x, y}` only — the snap is
 * just where the commit lands. The legacy `{edge, ratio}` model has
 * been retired (no dual-write).
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { Sparkles } from 'lucide-react';
import { useFocusContext } from '../focus/useFocusContext';
import { GLOSSARY } from '../glossary';
import { useToastContext } from '../../../core/ui/toast/ToastProvider';
import { useDragPosition } from './useDragPosition';

// 20px breathing room from the viewport edges on left/right/bottom —
// matches the canonical 20px canvas inset used by `.home-dash`,
// `.education-content`, and `.rail-layout__main`. Top edge uses the
// larger `TOPBAR_CLEARANCE` so the FAB clears the SurfaceHeader.
const FAB_MARGIN = 20;
const FAB_SIZE = 44;
const DRAG_THRESHOLD = 6;
const DESKTOP_BREAKPOINT = 768;
const TOPBAR_CLEARANCE = 80;

const FAB_ELEMENT_SIZE = { width: FAB_SIZE, height: FAB_SIZE };

function isDesktopViewport(): boolean {
    return typeof window !== 'undefined' && window.innerWidth >= DESKTOP_BREAKPOINT;
}

function mainContentLeftInset(): number {
    if (typeof document === 'undefined') return 0;
    const main = document.querySelector<HTMLElement>('.layout-main');
    if (!main) return 0;
    const rect = main.getBoundingClientRect();
    return Math.max(0, rect.left);
}

function clamp(n: number, lo: number, hi: number): number {
    return Math.max(lo, Math.min(hi, n));
}

type Viewport = { width: number; height: number };
type Position = { x: number; y: number };

/** Pure helper: compute snap-to-nearest-edge target for a raw `{x, y}`.
 *  Returns the snapped `{x, y}` accounting for the desktop sidebar
 *  inset (`leftEdgeInset`) and the SurfaceHeader top clearance.
 *
 *  Lifted out of the pointer handler so it can be unit-tested and so
 *  the imperative drag-end path stays a thin orchestrator. */
export function computeSnapTarget(
    raw: Position,
    viewport: Viewport,
    opts: { leftEdgeInset: number; desktop: boolean },
): Position {
    const { width: vw, height: vh } = viewport;
    const cx = raw.x + FAB_SIZE / 2;
    const cy = raw.y + FAB_SIZE / 2;
    const distances = {
        left: cx,
        right: vw - cx,
        top: cy,
        bottom: vh - cy,
    } as const;
    const edges: ReadonlyArray<keyof typeof distances> = ['left', 'right', 'top', 'bottom'];
    const best = edges.reduce(
        (acc, k) => (distances[k] < distances[acc] ? k : acc),
        'right' as keyof typeof distances,
    );

    const leftEdge = opts.desktop ? opts.leftEdgeInset + FAB_MARGIN : FAB_MARGIN;
    const rightEdge = Math.max(leftEdge, vw - FAB_SIZE - FAB_MARGIN);
    const minTop = opts.desktop ? TOPBAR_CLEARANCE : FAB_MARGIN;
    const maxTop = Math.max(minTop, vh - FAB_SIZE - FAB_MARGIN);

    switch (best) {
        case 'left':
            return { x: leftEdge, y: clamp(raw.y, minTop, maxTop) };
        case 'right':
            return { x: rightEdge, y: clamp(raw.y, minTop, maxTop) };
        case 'top':
            return { x: clamp(raw.x, leftEdge, rightEdge), y: minTop };
        case 'bottom':
            return { x: clamp(raw.x, leftEdge, rightEdge), y: maxTop };
    }
}

/** How long the FAB pulses after a focus change. Long enough for the
 *  mentor to notice + short enough not to feel like spam. */
const PULSE_DURATION_MS = 6000;

/** Default landing spot for a brand-new install — right-edge, ~85% down
 *  the viewport. Matches the legacy `{ edge: 'right', ratio: 0.85 }`
 *  default so first-time users see the same starting position. */
function computeDefaultPosition(): Position {
    if (typeof window === 'undefined') return { x: 0, y: 0 };
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    return {
        x: Math.max(FAB_MARGIN, vw - FAB_SIZE - FAB_MARGIN),
        y: clamp(vh * 0.85 - FAB_SIZE / 2, TOPBAR_CLEARANCE, vh - FAB_SIZE - FAB_MARGIN),
    };
}

export default function DraggableMentalityFab({ onOpen }: { onOpen: () => void }) {
    // Compute the default lazily so SSR / non-window environments don't
    // throw. The `useState` initializer runs once and feeds
    // `useDragPosition` a stable seed.
    const [defaultPosition] = useState<Position>(() => computeDefaultPosition());
    // Wire toast SSOT into the persistence path so a QuotaExceededError
    // (Safari private mode, full storage) surfaces a one-shot toast.
    // The hook throttles internally via sessionStorage so we never
    // double-fire across a session.
    const toast = useToastContext();
    const handlePersistQuotaExceeded = useCallback(
        (message: string) => {
            toast.error(message);
        },
        [toast],
    );
    const { position, setPosition, commitPosition } = useDragPosition({
        defaultPosition,
        elementSize: FAB_ELEMENT_SIZE,
        onPersistQuotaExceeded: handlePersistQuotaExceeded,
    });
    const [dragging, setDragging] = useState(false);
    const movedRef = useRef(false);
    const startRef = useRef<{ x: number; y: number; offsetX: number; offsetY: number } | null>(null);

    // ── Phase 5b — FAB pulse on focus change ─────────────────────────
    //
    //  When the mentor focuses on a new entity (opens a mentee, etc.),
    //  pulse the FAB for a few seconds so they know the AI coach has
    //  guidance scoped to that entity. Stops on:
    //    - PULSE_DURATION_MS timeout
    //    - FAB click (opening the panel acknowledges the signal)
    //    - Focus clears (nothing to pulse about anymore)
    const { focus } = useFocusContext();
    const [pulsing, setPulsing] = useState(false);
    const lastFocusIdRef = useRef<string | null>(null);
    useEffect(() => {
        const nextId = focus?.id ?? null;
        if (nextId === lastFocusIdRef.current) return;
        lastFocusIdRef.current = nextId;
        // Why: focus arrives via context (set by MentorReviewDashboard),
        // not props — the event/handler pattern can't express this seam.
        // Intentional setState-in-effect (see docs/mentorship-flow.md) drives
        // an attention-grabbing animation; state is purely visual.
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setPulsing(nextId !== null);
        if (nextId === null) return;
        const handle = window.setTimeout(() => setPulsing(false), PULSE_DURATION_MS);
        return () => window.clearTimeout(handle);
    }, [focus?.id]);

    // Sidebar open/close changes `.layout-main`'s left margin without
    // firing a window resize. Observe `.layout-main` and re-emit a
    // window `resize` event so `useDragPosition`'s clamp logic runs —
    // otherwise a left-edge FAB ends up under the sidebar after the
    // user toggles it open, or floats far from the canvas after
    // collapse. Re-using the existing resize listener keeps the clamp
    // logic in one place (the hook).
    useEffect(() => {
        if (typeof document === 'undefined') return;
        const main = document.querySelector<HTMLElement>('.layout-main');
        if (!main || typeof ResizeObserver === 'undefined') return;
        const ro = new ResizeObserver(() => {
            window.dispatchEvent(new Event('resize'));
        });
        ro.observe(main);
        return () => ro.disconnect();
    }, []);

    const handlePointerDown = (e: React.PointerEvent<HTMLButtonElement>) => {
        if (e.button !== 0) return;
        (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
        movedRef.current = false;
        startRef.current = {
            x: e.clientX,
            y: e.clientY,
            offsetX: e.clientX - position.x,
            offsetY: e.clientY - position.y,
        };
    };

    const handlePointerMove = (e: React.PointerEvent<HTMLButtonElement>) => {
        if (!startRef.current) return;
        const dx = e.clientX - startRef.current.x;
        const dy = e.clientY - startRef.current.y;
        if (!movedRef.current && Math.hypot(dx, dy) >= DRAG_THRESHOLD) {
            movedRef.current = true;
            setDragging(true);
        }
        if (!movedRef.current) return;
        const vw = window.innerWidth;
        const vh = window.innerHeight;
        const desktop = isDesktopViewport();
        const minLeft = desktop ? mainContentLeftInset() : 0;
        const minTop = desktop ? TOPBAR_CLEARANCE : 0;
        const maxTop = vh - FAB_SIZE;
        const x = clamp(e.clientX - startRef.current.offsetX, minLeft, vw - FAB_SIZE);
        const y = clamp(e.clientY - startRef.current.offsetY, minTop, maxTop);
        // Why: live drag updates the SSOT in-memory only — the
        // `useDragPosition` hook splits setPosition (no-write) from
        // commitPosition (write-once-on-end). Writing every frame
        // would thrash localStorage at 60Hz and breaks the hook
        // contract — see useDragPosition.ts docstring and the
        // "10 setPosition + 1 commit writes exactly ONCE" test in
        // useDragPosition.test.tsx which pins this guarantee.
        setPosition({ x, y });
    };

    const handlePointerUp = (e: React.PointerEvent<HTMLButtonElement>) => {
        (e.currentTarget as HTMLElement).releasePointerCapture?.(e.pointerId);
        if (!startRef.current) return;

        if (!movedRef.current) {
            startRef.current = null;
            setDragging(false);
            // Opening the panel acknowledges the focus-change signal —
            // stop pulsing immediately so we don't keep flashing while
            // the panel is open.
            setPulsing(false);
            onOpen();
            return;
        }

        const desktop = isDesktopViewport();
        const snapped = computeSnapTarget(
            position,
            { width: window.innerWidth, height: window.innerHeight },
            { leftEdgeInset: mainContentLeftInset(), desktop },
        );
        // Commit-on-end: the SSOT clamps to viewport + writes to
        // localStorage exactly once per drag.
        commitPosition(snapped);
        setDragging(false);
        startRef.current = null;
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setPulsing(false);
            onOpen();
        }
    };

    const fabStyle = {
        '--fab-left': `${position.x}px`,
        '--fab-top': `${position.y}px`,
    } as React.CSSProperties;

    // Compose the aria-label from the GLOSSARY SSOT + the "Hold to
    // move" suffix so screen readers announce the dual purpose
    // (open + draggable) in one breath. The "Hold to move" suffix is
    // assertion-grade copy pinned by the FAB a11y tests.
    const ariaLabel = `Open ${GLOSSARY.MENTALITY.shortLabel} (Hold to move)`;

    return (
        <button
            type="button"
            className={`mentality-overlay__fab mentality-overlay__fab--draggable${dragging ? ' is-dragging' : ''}${pulsing ? ' is-pulsing' : ''}`}
            style={fabStyle}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onPointerCancel={handlePointerUp}
            onKeyDown={handleKeyDown}
            aria-label={ariaLabel}
            title={GLOSSARY.MENTALITY.shortLabel}
            data-tour="mentality-fab"
        >
            <Sparkles size={18} aria-hidden="true" />
            {/* Visible label — sourced from the GLOSSARY SSOT so the
             *  rendered string stays in lockstep with the rest of the
             *  workspace. We previously wrapped this in `<ConceptChip
             *  term="MENTALITY" />` for the hover-tooltip glossary
             *  popover, but ConceptChip's inner span carries
             *  `role="button"` + `tabIndex={0}` to handle its own
             *  focus/tap-pin contract — and nesting that interactive
             *  span inside the FAB's `<button>` is an axe
             *  `nested-interactive` violation (WS-ENT-4 baseline
             *  gate). The FAB's `aria-label` is already the
             *  authoritative accessible name; the popover affordance
             *  belongs on inline copy chips, not on a primary FAB
             *  whose entire surface IS the click target. */}
            <span
                className="mentality-overlay__fab-label"
                aria-hidden="true"
            >
                {GLOSSARY.MENTALITY.shortLabel}
            </span>
        </button>
    );
}
